import { faXmarkCircle, faPlus, faTrash, faFloppyDisk, faPenToSquare, faXmark, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { getSaiTerminology } from '../../../../config/terminology'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTheme } from '../../../../context/ThemeProvider'
import { useState, useEffect } from 'react'

import useAxiosPrivateKIS from '../../../../hooks/useAxiosPrivateKIS'
import useAuth from '../../../../hooks/useAuth'

const ViewContacts = ({ open, policyData }) => {
  const { auth, createInfo } = useAuth()

  const theme = useTheme()
  const axios = useAxiosPrivateKIS()
  const terms = getSaiTerminology(policyData)

  const [newContact, setNewContact] = useState({ name: '', email: '', position: '' })
  const [selectedSchools, setSelectedSchools] = useState([])

  const [policyInfo, setPolicyInfo] = useState({})

  const [editingIndex, setEditingIndex] = useState(null)
  const [editingEmail, setEditingEmail] = useState('')
  const [editingName, setEditingName] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [schoolData, setSchoolData] = useState([])
  const [isAdding, setIsAdding] = useState(false)

  const [contacts, setContacts] = useState([])

  const panelStyle = {
    color: theme.textColor,
  }

  const accentStyle = {
    backgroundColor: theme.backgroundColor,
    borderColor: theme.backgroundColor,
    color: '#ffffff',
  }

  const softAccentStyle = {
    backgroundColor: `${theme.backgroundColor}14`,
    borderColor: `${theme.backgroundColor}44`,
  }

  useEffect(() => {
    if (open) fetchContacts()
  }, [open])

  useEffect(() => {
    if (policyData) {
      setPolicyInfo(JSON.parse(policyData?.policy_information || '{}'))
    }
  }, [policyData])

  const fetchContacts = async () => {
    try {
      setIsLoading(true)
      const orgId = policyData.policyable_id
      if (!orgId) return

      const res = await axios.get(`/api/v1/staff-hub/policys/contacts?current_id=${orgId}&api_token=${process.env.REACT_APP_KIS_TOKEN}`)
      setContacts(res.data.contacts)
      setSchoolData(res.data.schoolData?.schools || [])
    } catch (err) {
      createInfo('error', 'Failed to fetch contacts')
    }
    setIsLoading(false)
  }

  const handleAddContact = async () => {
    const { name, email, position } = newContact

    if (!name || !email || !position) {
      createInfo('error', 'Please fill out all fields.')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      createInfo('error', 'Please enter a valid email address.')
      return
    }

    const validSchoolData = Array.isArray(schoolData) && schoolData.length > 0 ? schoolData : false

    const payload = {
      contact_id: 0,
      contact_name: name,
      contact_email: email,
      contact_position: position,
      school_ids: validSchoolData ? selectedSchools : [policyData?.policyable_id],
      type: 'academy',
      cid: JSON.parse(localStorage.getItem('orgType'))?.id,
      roles: [1, 2, 3, 4],
    }

    try {
      const response = await axios.post(`/api/v1/staff-hub/policys/contacts/new?api_token=${process.env.REACT_APP_KIS_TOKEN}`, payload)
      if (response.status === 200 || response.data?.success) {
        createInfo('success', 'Contact added successfully.')
        fetchContacts()
        setIsAdding(false)
      } else {
        createInfo('error', 'Failed to add contact. Please try again.')
      }
    } catch (error) {
      console.error('Error adding contact:', error)
      createInfo('error', 'Failed to add contact.')
    }
  }

  const handleUpdateContact = async (index) => {
    const contact = contacts[index]
    try {
      await axios.post(`/api/v1/staff-hub/policys/contacts/update?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        contact_id: contact.contact_id,
        contact_name: editingName,
        contact_email: editingEmail,
        type: 'academy',
        cid: JSON.parse(localStorage.getItem('orgType'))?.id,
      })
      createInfo('success', 'Contact updated successfully.')
      fetchContacts()
    } catch (error) {
      createInfo('error', 'Update failed')
    }
  }

  const handleDeleteContact = async (index) => {
    const contact = contacts[index]
    try {
      await axios.post(`/api/v1/staff-hub/policys/contacts/delete?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        contact_id: contact.contact_id,
        type: 'academy',
        cid: JSON.parse(localStorage.getItem('orgType'))?.id,
      })
      createInfo('success', 'Contact deleted.')
      fetchContacts()
    } catch (error) {
      createInfo('error', 'Delete failed')
    }
  }

  return (
    <div className="space-y-5" style={panelStyle}>
      <div className="rounded-xl border p-4 sm:p-5" style={softAccentStyle}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-gray-600">
            Manage who receives {terms.policy.singularLower} communications
          </p>
          {!isAdding && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="inline-flex items-center rounded-lg overflow-hidden text-sm font-semibold shadow-sm border"
              style={{ borderColor: theme.backgroundColor }}>
              <span className="px-4 py-2 bg-white" style={{ color: theme.backgroundColor }}>Add Contact</span>
              <span className="px-3 py-2" style={accentStyle}>
                <FontAwesomeIcon icon={faPlus} />
              </span>
            </button>
          )}
        </div>
      </div>

      {isAdding && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Name</label>
              <input
                className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sas/25 focus:border-sas"
                placeholder="Name"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
              <input
                className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sas/25 focus:border-sas"
                placeholder="Email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Position</label>
            <input
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sas/25 focus:border-sas"
              placeholder="Position"
              value={newContact.position}
              onChange={(e) => setNewContact({ ...newContact, position: e.target.value })}
            />
          </div>
          {schoolData.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Assign schools</p>
              <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                  {schoolData.map((school, idx) => (
                    <label key={idx} className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        value={school.id}
                        checked={selectedSchools.includes(school.id)}
                        onChange={(e) => {
                          const checked = e.target.checked
                          const id = school.id
                          setSelectedSchools((prev) =>
                            checked ? [...prev, id] : prev.filter((s) => s !== id)
                          )
                        }}
                        className="mr-2"
                      />
                      <span className="truncate">{school.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">School</p>
              <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                  { policyInfo?.name }
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="inline-flex items-center rounded-lg overflow-hidden text-sm font-semibold border border-red-700 text-red-700">
              <span className="px-4 py-2 bg-white">Cancel</span>
              <span className="px-3 py-2 bg-red-700 text-white">
                <FontAwesomeIcon icon={faXmark} />
              </span>
            </button>
            <button
              type="button"
              onClick={handleAddContact}
              className="inline-flex items-center rounded-lg overflow-hidden text-sm font-semibold border"
              style={{ borderColor: theme.backgroundColor }}>
              <span className="px-4 py-2 bg-white" style={{ color: theme.backgroundColor }}>Save Contact</span>
              <span className="px-3 py-2" style={accentStyle}>
                <FontAwesomeIcon icon={faCheck} />
              </span>
            </button>
          </div>
        </div>
      )}

      <div>
        {isLoading ? (
          <div className="rounded-xl border border-gray-200 bg-white h-44 flex justify-center items-center text-gray-500">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        ) : (
          <>
            {!isAdding && (
              <div className="space-y-3">
                <div className="rounded-xl border p-4 bg-white" style={softAccentStyle}>
                  <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Primary contact</p>
                  <p className="font-semibold mt-1" style={{ color: theme.backgroundColor }}>{auth.user.contact_name} (You)</p>
                  <p className="text-sm text-gray-700">{auth.user.contact_email}</p>
                </div>

                {contacts.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                    <p className="font-semibold text-gray-700">No additional contacts yet</p>
                    <p className="text-sm text-gray-600 mt-1">Use Add Contact to create your first contact.</p>
                  </div>
                )}

                {contacts.map((contact, idx) => (
                  <div
                    key={idx}
                    className={`border p-4 rounded-xl shadow-sm bg-white ${contact.contact_status === 'disabled' ? 'border-orange-300' : 'border-gray-200'
                      }`}
                  >
                    {editingIndex === idx ? (
                      <div className="space-y-2">
                        <div className="flex justify-end text-sm">
                          <FontAwesomeIcon
                            icon={faXmarkCircle}
                            className="text-red-600 cursor-pointer"
                            onClick={() => setEditingIndex(null)}
                          />
                        </div>
                        <label className='text-xs font-semibold text-gray-600' htmlFor="name">Name</label>
                        <input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="w-full border border-gray-300 px-3 py-2 rounded-lg"
                        />
                        <label className='text-xs font-semibold text-gray-600' htmlFor="name">Email</label>
                        <input
                          value={editingEmail}
                          onChange={(e) => setEditingEmail(e.target.value)}
                          className="w-full border border-gray-300 px-3 py-2 rounded-lg"
                        />
                        <div className="flex justify-end gap-4 pt-1">
                          <FontAwesomeIcon
                            icon={faFloppyDisk}
                            className="text-green-600 cursor-pointer text-lg"
                            onClick={() => handleUpdateContact(idx)}
                          />
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-red-500 cursor-pointer text-lg"
                            onClick={() => handleDeleteContact(idx)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-start gap-3">
                        <div>
                          <p className="font-semibold mb-1" style={{ color: theme.backgroundColor }}>{contact.contact_name}</p>
                          <p className="text-sm text-gray-700">{contact.contact_email}</p>
                          {contact.contact_status === 'disabled' && (
                            <p className="inline-block text-xs text-orange-700 font-semibold mt-2 px-2 py-1 rounded-full bg-orange-100">Pending update</p>
                          )}
                        </div>
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          className={`${contact.contact_status === 'disabled' ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                          style={contact.contact_status === 'disabled' ? undefined : { color: theme.backgroundColor }}
                          onClick={() => {
                            if (contact.contact_status !== 'disabled') {
                              setEditingIndex(idx)
                              setEditingName(contact.contact_name)
                              setEditingEmail(contact.contact_email)
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ViewContacts

